import React, { useMemo, useState, useEffect } from 'react';
import { Card, Layout } from 'tea-component';
import { useIntl } from 'umi';
import Chart from '@/pages/dashboard/components/Chart';
import { STUDIO_CAPABILITY_DATA, STUDIO_TYPE_CONFIG } from './const/studioCapability';
import { SPECIAL_STUDIO_NAME, STUDIO_ICON_MAP } from '../worldMap/const/iconMap';
import './index.scss';
import { createRoundedImage } from '../worldMap/utils/badgeGenerator';
import { escapeHtml } from '../utils/sanitizer';
import { buriedPointPage } from '@home/utils/buriedPoint';

const { Content } = Layout;

interface IRichStyle {
  [key: string]: {
    fontSize?: number;
    fontWeight?: number;
    color?: string;
    backgroundColor?: { image: string; };
    width?: number;
    height?: number;
    align?: string;
    padding?: number[];
  };
}

interface IStudioPointsData {
  value: [number, number];
  name: string;
  subStudio?: string;
}

interface IEChartsSeriesItem {
  name: string;
  type: 'scatter';
  data: IStudioPointsData[];
  label: {
    show: boolean;
    formatter: (params: { name: string; }) => string;
    rich: IRichStyle;
    align?: string;
    padding?: number[] | number;
  };
  symbolSize: number;
  markArea?: any;
  itemStyle?: {
    color?: string;
  };
}

const Matrix = () => {
  const { formatMessage } = useIntl();
  const [roundIconMap, setRoundIconMap] = useState<Record<string, string>>({});

  useEffect(() => {
    buriedPointPage({
      pageId: '208002',
      pageName: 'STH Matrix',
      extInfo: '',
      pagePath: '/sth/matrix',
      typeId: "dashboard",
    });
  }, []);

  // Generate round icons asynchronously
  useEffect(() => {
    const generateRoundIcons = async () => {
      const iconMap: Record<string, string> = {};

      const results = await Promise.allSettled(
        STUDIO_CAPABILITY_DATA.map(async (studio) => {
          const studioName = studio?.sub_studio || studio.name;
          const name = SPECIAL_STUDIO_NAME?.[studio.name] || studio.name;
          const iconPath = STUDIO_ICON_MAP[studioName];

          if (iconPath) {
            const roundIcon = await createRoundedImage(iconPath, 120, 20, '#ffffff');
            return { name, roundIcon };
          }
          return null;
        }),
      );

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          const { name, roundIcon } = result.value;
          iconMap[name] = roundIcon;
        }
      });

      setRoundIconMap(iconMap);
    };

    generateRoundIcons();
  }, []);

  // 按类型统计数量（用于标题右侧展示）
  const typeCounts = useMemo(() => {
    return STUDIO_TYPE_CONFIG.map((config) => ({
      label: config.label,
      color: config.color,
      count: STUDIO_CAPABILITY_DATA.filter((s) => s.type === config.type).length,
    }));
  }, []);

  // 构建 ECharts scatter 配置
  const chartOption = useMemo(() => {
    // 按类型分组数据，用于散点图
    const studioPointsData = STUDIO_CAPABILITY_DATA.map((studio) => {
      return {
        value: [studio.quality, studio.efficiency] as [number, number],
        name: studio.name,
        subStudio: studio.sub_studio,
      };
    });

    // Build rich styles for labels with icons
    const richStyles: IRichStyle = {
      text: {
        fontSize: 10,
        fontWeight: 800,
        color: '#000',
        align: 'center',
        padding: [0, 5],
      },
    };

    // Add icon styles for each studio
    STUDIO_CAPABILITY_DATA.forEach((studio) => {
      const name = SPECIAL_STUDIO_NAME?.[studio.name] || studio.name;
      const roundIcon = roundIconMap[name];

      if (roundIcon) {
        richStyles[`${name}`] = {
          backgroundColor: {
            image: roundIcon,
          },
          width: 24,
          height: 24,
        };
      }
    });

    return {
      backgroundColor: 'transparent',
      grid: {
        left: 10,
        right: 50,
        bottom: 80,
        top: 30,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'Quality',
        nameLocation: 'end',
        nameGap: 10,
        nameTextStyle: {
          color: '#838697',
          fontSize: 12,
          fontWeight: 400,
        },
        min: 0,
        max: 100,
        interval: 10,
        axisLine: {
          lineStyle: {
            type: 'solid',
            color: 'rgba(0,0,0,0.25)',
            width: 1, // 坐标线的宽度
          },
          onZero: false,
        },
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          color: '#838697',
          fontSize: 12,
          align: 'center',
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Efficiency',
        nameLocation: 'end',
        nameGap: 15,
        nameTextStyle: {
          color: '#838697',
          fontSize: 12,
          fontWeight: 400,
        },
        min: 0,
        max: 100,
        interval: 10,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#E5E5E5',
          },
        },
        axisLabel: {
          color: '#838697',
          fontSize: 12,
        },
      },
      legend: {
        show: true,
        bottom: 50,
        left: 'center',
        orient: 'horizontal',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 24,
        textStyle: {
          color: '#6B7280',
          fontSize: 12,
          fontWeight: 400,
        },
        icon: 'circle',
        data: STUDIO_TYPE_CONFIG.filter((c) => c.showInLegend !== false).map(
          (c) => c.label,
        ),
      },
      tooltip: {
        trigger: 'item',
        confine: true,
        backgroundColor: '#2f3136',
        borderColor: '#2f3136',
        borderRadius: 8,
        padding: [12, 16],
        textStyle: {
          color: '#fff',
          fontSize: 12,
        },
        formatter: (params: any) => {
          if (params.componentSubType !== 'scatter' || !params.value) return '';
          const { name, value, data } = params;
          const subStudio = data?.subStudio;
          const iconName = SPECIAL_STUDIO_NAME?.[name] || name;
          const iconPath = STUDIO_ICON_MAP[iconName];
          const subStudioIconPath = subStudio ? STUDIO_ICON_MAP[subStudio] : null;
          const iconHtml = iconPath
            ? `<img src="${iconPath}" class="dbd-sth-matrix-tooltip__icon" />`
            : '';
          const subStudioHtml = subStudioIconPath
            ? `<div class="dbd-sth-matrix-tooltip__substudio">
                <img src="${subStudioIconPath}" class="dbd-sth-matrix-tooltip__icon" />
                <span class="dbd-sth-matrix-tooltip__substudio-label">${escapeHtml(subStudio)}</span>
              </div>`
            : '';
          return `
            <div class="dbd-sth-matrix-tooltip">
              <div class="dbd-sth-matrix-tooltip__header">
                <div class="dbd-sth-matrix-tooltip__header-content">
                  ${iconHtml}<span>${escapeHtml(name)}</span>
                </div>
                ${subStudioHtml}
              </div>
              <div class="dbd-sth-matrix-tooltip__metric">Efficiency <span class="dbd-sth-matrix-tooltip__metric-value">${value[1]}</span></div>
              <div class="dbd-sth-matrix-tooltip__metric">Quality <span class="dbd-sth-matrix-tooltip__metric-value">${value[0]}</span></div>
            </div>
          `;
        },
      },
      series: STUDIO_TYPE_CONFIG.map((config) => {
        const seriesItem: IEChartsSeriesItem = {
          name: config.label,
          type: 'scatter',
          data: studioPointsData.filter((d) => {
            const studio = STUDIO_CAPABILITY_DATA.find((s) => s.name === d.name);
            return studio?.type === config.type;
          }),
          label: {
            show: true,
            formatter: (params: any) => {
              const { name: studioName, subStudio } = params.data || {};
              const displayName = subStudio ? `${studioName}+${subStudio}` : studioName;
              const iconName = SPECIAL_STUDIO_NAME?.[studioName] || studioName;
              if (roundIconMap[iconName]) {
                return `{${iconName}|}{text|${displayName}}`;
              }
              return displayName;
            },
            rich: richStyles,
            align: 'left',
            padding: [0, 0, 0, -12],
          },
          itemStyle: {
            color: config.color,
          },
          symbolSize: 0,
        };

        // Add markArea only if data exists and is not empty
        if (config.data && config.data.length > 0 && config.markAreaColorStops) {
          seriesItem.markArea = {
            silent: true,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: config.markAreaColorStops,
              },
            },
            data: [config.data],
          };
        }

        return seriesItem;
      }),
    } as any;
  }, [roundIconMap]);

  return (
    <Content>
      <Content.Body full className="dbd-scroll-body">
        <Card className="dbd-sth-matrix-content">
          <div className="dbd-sth-matrix-title">
            <span>{formatMessage({ id: 'studio_capability_evaluation' })}</span>
            <div className="dbd-sth-matrix-title__counts">
              {typeCounts.map((item) => (
                <span key={item.label} className="dbd-sth-matrix-title__count-item">
                  <span
                    className="dbd-sth-matrix-title__count-dot"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="dbd-sth-matrix-title__count-label">{item.label}</span>
                  <span className="dbd-sth-matrix-title__count-value">{item.count}</span>
                </span>
              ))}
            </div>
          </div>
          <Chart option={chartOption} notMerge={true} />
        </Card>
      </Content.Body>
    </Content>
  );
};

export default Matrix;